/**
 * A card that opens beside its trigger — who a teammate is, what a section
 * holds.
 *
 * It describes the trigger rather than interrupting: it opens after a short
 * pause on hover, so a pointer passing over does not flash it — at once when
 * another card has only just closed, so a pointer running down the rail reads
 * each in turn; at once on keyboard focus (focus a click or a script gives
 * is not asked for — a popover handing focus back as it closes); and on a
 * long press on touch, so a tap still does what the trigger does. The trigger names the card as its description.
 * Escape closes it, through the same stack as every dialog and popover, so
 * over a dialog it closes the card and not the dialog.
 *
 * A card that only describes lets the pointer through to the page beneath and
 * closes as the pointer leaves its trigger, so it never stands between a
 * player and what is under it — HQ's own tabs, a city on the globe. A card that
 * holds a control — your microphone — is `interactive`: it stays open while
 * the pointer travels into it, and Tab goes from the trigger into it and on to
 * whatever follows the trigger, as if it sat there.
 *
 *   <HoverCard label="Leo" placement="right" card={<>…name, budget, country…</>}>
 *     <button aria-label="Leo">…</button>
 *   </HoverCard>
 *
 * A caller may also hold it open for a moment, whatever the pointer does —
 * the rail's HQ card opens by itself when a quarter closes. `held` shows it;
 * Escape calls `onRelease`, and the caller lets go. Or keep it shut while
 * something else hangs from the trigger — your face's reactions: `suppressed`.
 *
 * Inside, `HoverCardTitle` names the card with the keyline under it, a
 * `HoverCardStatus` says what state the thing is in — "Locked during this
 * step", "Next update in 0:12" — each `HoverCardFact` is a label with its
 * value at the far end, and a `HoverCardNote` explains in a sentence or two:
 *
 *   <HoverCard label="Fleet" card={<>
 *     <HoverCardTitle>Fleet</HoverCardTitle>
 *     <HoverCardFact label="Aircraft owned" value="17" />
 *   </>}>…</HoverCard>
 *
 *   size     regular for a few facts; wide for a card with sections of its own
 *   surface  plate, the raised plate every card sits on; own, for content that
 *            is a card of its own and sets its own width
 *   stretch  the trigger takes its container's width — a rail item, which
 *            runs the width of the rail
 *
 * The open card describes its trigger to assistive technology. A card that
 * only appears on hover or focus says nothing to a screen reader browsing the
 * page, and a description that arrives a moment after focus is rarely read —
 * so where the card says why a control cannot act, pass that sentence as
 * `description`: it describes the trigger always, open or not, and the card
 * stays the visual. A description the trigger already has is kept beside it.
 *
 * The card renders into the body, as a popover does, and is placed beside its
 * trigger from there: a trigger inside a layer of its own — the rail, fixed to
 * the screen's edge — would otherwise keep its card at that layer's height,
 * under whatever the page stacks above it. Hanging from a moment, the live
 * window, a toast or a notice, it hangs on that layer's rung, after it, not
 * under it; hanging from any modal, it is inert with it while another is drawn
 * over it or it leaves; and hanging from the page, it renders with the page,
 * inert with it while any modal is open (useHang).
 */

import { cloneElement, isValidElement, useEffect, useId, useRef, useState } from 'react';
import type { HTMLAttributes, KeyboardEvent, ReactElement, ReactNode } from 'react';

import { firstFocusable, focusableAfter } from './focusable';
import { KeylineTick } from './keyline';
import { useHang } from './modal-layers';
import { useAnchoredPosition } from './use-anchored-position';
import type { Side } from '../../utils/clamp-to-viewport';
import { focusableWithin, useModalBehavior } from './use-modal-behavior';
import styles from './hover-card.module.css';

/** A pointer passing over does not open it. */
const OPEN_DELAY_MS = 120;
/** Time to travel from the trigger into an interactive card. */
const CLOSE_DELAY_MS = 160;
/** A card opening this soon after another closed opens at once: the pointer is reading along. */
const READING_ALONG_MS = 300;

/** When any card last closed, for READING_ALONG_MS. */
let lastClosedAt = 0;
/** How long a touch must rest to open it; a shorter tap is the trigger's own. */
const LONG_PRESS_MS = 450;

const SIZE = {
  regular: styles.regular,
  wide: styles.wide,
} as const;

const SURFACE = {
  plate: styles.plate,
  own: '',
} as const;

type HoverCardProps = {
  /** The one element it describes: a face, a rail section. */
  children: ReactElement<{ 'aria-describedby'?: string }>;
  placement?: Side;
  /** Show the card whatever the pointer does, until the caller lets go. */
  held?: boolean;
  /** Escape was pressed while the card was held: let go. */
  onRelease?: () => void;
  stretch?: boolean;
  /** The card holds a control: the pointer may travel into it, and Tab reaches it. */
  interactive?: boolean;
  /** A sentence that describes the trigger whether or not the card is open: why it rests. */
  description?: string;
  className?: string;
} & (
  | {
      /** The raised plate every card sits on, in one of its widths. */
      surface?: 'plate';
      size?: keyof typeof SIZE;
    }
  /** Content that is a card of its own, and sets its own width. */
  | { surface: 'own'; size?: never }
) &
  (
    | {
        /** What the card holds. */
        card: ReactNode;
        /** The card's accessible name. */
        label: string;
        /** Keep it shut, whatever the pointer does: something else hangs from the trigger now. */
        suppressed?: boolean;
      }
    /** No card, ever: a trigger kept in its place while it has nothing to say (Button, resting by turns). */
    | { card: null; label?: never; suppressed: true }
  );

export function HoverCard({
  children,
  card,
  label,
  placement = 'right',
  size = 'regular',
  surface = 'plate',
  held = false,
  onRelease,
  suppressed = false,
  stretch = false,
  interactive = false,
  description,
  className = '',
}: HoverCardProps) {
  const [hovered, setHovered] = useState(false);
  const open = !suppressed && (hovered || held);
  const anchor = useRef<HTMLSpanElement>(null);
  const layerRef = useAnchoredPosition<HTMLDivElement>(anchor, placement, open);
  // Hanging from a moment, the live window or a notice: on its rung, after it;
  // and inert with the modal it hangs from while another is drawn over it, or
  // while it leaves — or with the page, under any modal.
  const hang = useHang();
  const timer = useRef<number | undefined>(undefined);
  // A long press opened the card: the click that follows is not the trigger's.
  const swallowClick = useRef(false);
  const id = useId();
  const descriptionId = useId();

  const settle = (next: boolean, delay: number) => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setHovered(next), delay);
  };
  useEffect(() => () => window.clearTimeout(timer.current), []);

  // Escape through the shared stack, topmost first — for a card the player
  // opened. One the caller holds open was not asked for, and must not take the
  // key from whatever the player is doing: dragging a route, say. Its ref is
  // on the card, so a card gone inert with what it hangs from — a moment
  // fading out — gives the key up (use-modal-behavior.ts, reachableOnTop).
  const behaviourRef = useModalBehavior<HTMLDivElement>({
    isOpen: hovered && !suppressed,
    onDismiss: () => {
      setHovered(false);
      if (held) onRelease?.();
    },
    lockBackgroundScroll: false,
    trapFocus: false,
  });

  // What describes the trigger: its own description, then the sentence that
  // always does — or, without one, the card while it is open.
  const own = isValidElement(children) ? children.props['aria-describedby'] : undefined;
  const describedBy = [own, description ? descriptionId : open ? id : undefined].filter(Boolean).join(' ') || undefined;
  const trigger = isValidElement(children) ? cloneElement(children, { 'aria-describedby': describedBy }) : children;

  // An interactive card is not beside its trigger in the page, so Tab is
  // walked by hand: from the trigger into the card; back out of its first item
  // to the trigger; and on from its last to what follows the trigger. React
  // carries the key presses up from the card to here.
  const onKeyDown = (event: KeyboardEvent) => {
    const panel = layerRef.current;
    const from = anchor.current;
    if (event.key !== 'Tab' || !open || !interactive || !panel || !from) return;
    const items = focusableWithin(panel);
    const target = event.target as HTMLElement;
    if (!panel.contains(target)) {
      if (!event.shiftKey && items.length > 0) {
        event.preventDefault();
        items[0].focus();
      }
      return;
    }
    const at = items.indexOf(target);
    if (event.shiftKey && at <= 0) {
      event.preventDefault();
      firstFocusable(from)?.focus();
    } else if (!event.shiftKey && at === items.length - 1) {
      event.preventDefault();
      focusableAfter(from, panel)?.focus();
    }
  };

  return (
    <span
      className={[styles.hoverCard, stretch ? styles.stretch : '', className].filter(Boolean).join(' ')}
      onPointerEnter={(event) =>
        event.pointerType !== 'touch' && settle(true, Date.now() - lastClosedAt < READING_ALONG_MS ? 0 : OPEN_DELAY_MS)
      }
      onPointerLeave={(event) => {
        if (event.pointerType === 'touch') return;
        // Now, not when the close lands: the next trigger's enter comes in the
        // same event, and reads it.
        if (open) lastClosedAt = Date.now();
        settle(false, interactive ? CLOSE_DELAY_MS : 0);
      }}
      onPointerDown={(event) => {
        if (event.pointerType !== 'touch') return;
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => {
          swallowClick.current = true;
          setHovered(true);
        }, LONG_PRESS_MS);
      }}
      onPointerUp={(event) => event.pointerType === 'touch' && !hovered && window.clearTimeout(timer.current)}
      onClickCapture={(event) => {
        if (!swallowClick.current) return;
        swallowClick.current = false;
        event.preventDefault();
        event.stopPropagation();
      }}
      onFocus={(event) => (event.target as HTMLElement).matches(':focus-visible') && settle(true, 0)}
      onBlur={(event) => {
        // Moving focus between the trigger and the card is not leaving.
        const next = event.relatedTarget as Node | null;
        if (!event.currentTarget.contains(next) && !layerRef.current?.contains(next)) settle(false, 0);
      }}
      onKeyDown={onKeyDown}
    >
      <span ref={anchor} className={styles.trigger}>
        {trigger}
      </span>
      {/* Hidden, not merely out of sight: a description is read through the
          trigger's aria-describedby, which reads a hidden element, and a
          trigger inside a tablist must not carry stray text beside it. */}
      {description && (
        <span id={descriptionId} hidden>
          {description}
        </span>
      )}
      {open &&
        hang.portal(
          <div
            ref={(element) => {
              layerRef.current = element;
              behaviourRef.current = element;
              const letGo = hang.from(element);
              return () => {
                layerRef.current = null;
                behaviourRef.current = null;
                letGo?.();
              };
            }}
            id={id}
            role="group"
            aria-label={label}
            className={[styles.card, SURFACE[surface], surface === 'plate' ? SIZE[size] : '', interactive ? '' : styles.passThrough]
              .filter(Boolean)
              .join(' ')}
            style={hang.style}
          >
            {card}
          </div>,
        )}
    </span>
  );
}

/**
 * A hover card's plate standing still, where nothing anchors or opens it: a
 * chart's readout beside its crosshair, the card for the flight being read.
 * Where it sits and how wide it runs are the caller's; the plate is the card's.
 */
export function HoverCardPlate({ className = '', ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={[styles.plate, className].filter(Boolean).join(' ')} {...rest} />;
}

/** A card's name, anything that belongs at the far end of it, and the keyline under both. */
export function HoverCardTitle({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div className={styles.titleBlock}>
      <div className={styles.titleRow}>
        <strong className={styles.title}>{children}</strong>
        {aside}
      </div>
      <KeylineTick length="full" />
    </div>
  );
}

const STATUS_TONE = {
  neutral: '',
  warning: styles.warning,
  danger: styles.danger,
} as const;

/**
 * The state the thing is in, under the title: a mark — an icon, a spinner, a
 * dot — and a few words. `tone` colours it when the state is a problem.
 */
export function HoverCardStatus({ children, tone = 'neutral' }: { children: ReactNode; tone?: keyof typeof STATUS_TONE }) {
  return <p className={[styles.status, STATUS_TONE[tone]].filter(Boolean).join(' ')}>{children}</p>;
}

/** One fact on a card: what it is, and its value at the far end. */
export function HoverCardFact({ label, value }: { label: ReactNode; value: ReactNode }) {
  return (
    <div className={styles.fact}>
      <span>{label}</span>
      <span className={styles.value}>{value}</span>
    </div>
  );
}

/** A sentence or two about the thing: why it is so, what happens next. */
export function HoverCardNote({ children }: { children: ReactNode }) {
  return <p className={styles.note}>{children}</p>;
}
